import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { practiceAreas } from '../../mocks/practice-areas';

const PracticeAreaDetailPage = () => {
  const { slug } = useParams();
  const area = practiceAreas.find((a) => a.slug === slug);

  if (!area) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-serif text-4xl text-navy-900 mb-4">Uzmanlık Alanı Bulunamadı</h1>
          <Link to="/practice-areas" className="text-gold-500 hover:text-gold-600 cursor-pointer">
            Uzmanlık Alanlarına Dön
          </Link>
        </div>
      </div>
    );
  }

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-navy-900">
        <div className="absolute inset-0 opacity-20">
          <img
            src={area.image}
            alt={area.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-navy-900/80"></div>
        </div>
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link
              to="/practice-areas"
              className="inline-flex items-center text-gold-500 hover:text-gold-400 mb-8 cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              <span className="font-sans text-sm">Tüm Uzmanlık Alanlarına Dön</span>
            </Link>
            <div className="w-16 h-16 flex items-center justify-center bg-gold-500 rounded-lg mb-6">
              <i className={`${area.icon} text-3xl text-white`}></i>
            </div>
            <h1 className="font-serif text-5xl lg:text-6xl text-white mb-6">{area.title}</h1>
            <p className="font-sans text-xl text-white/90 max-w-3xl">{area.shortDescription}</p>
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <motion.div {...fadeInUp}>
            <h2 className="font-serif text-3xl text-navy-900 mb-6">Genel Bakış</h2>
            <p className="font-sans text-base text-gray-700 leading-relaxed">{area.fullDescription}</p>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <motion.div {...fadeInUp}>
            <h2 className="font-serif text-3xl text-navy-900 mb-8">Hizmetlerimiz</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {area.services.map((service, index) => (
                <div
                  key={index}
                  className="flex items-start p-4 bg-white rounded-lg shadow-sm"
                >
                  <i className="ri-checkbox-circle-fill text-gold-500 mr-3 mt-1 flex-shrink-0"></i>
                  <span className="font-sans text-base text-gray-700">{service}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Common Issues */}
      <section className="py-16 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <motion.div {...fadeInUp}>
            <h2 className="font-serif text-3xl text-navy-900 mb-8">Sık Karşılaşılan Hukuki Meseleler</h2>
            <div className="space-y-4">
              {area.commonIssues.map((issue, index) => (
                <div
                  key={index}
                  className="flex items-start p-6 bg-gray-50 rounded-lg"
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-gold-500 rounded-full mr-4 flex-shrink-0">
                    <span className="font-sans text-sm font-bold text-white">{index + 1}</span>
                  </div>
                  <p className="font-sans text-base text-gray-700">{issue}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How We Help */}
      <section className="py-16 bg-navy-900">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <motion.div {...fadeInUp}>
            <h2 className="font-serif text-3xl text-white mb-6">Size Nasıl Yardımcı Oluyoruz</h2>
            <p className="font-sans text-base text-white/90 leading-relaxed mb-8">{area.howWeHelp}</p>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 bg-gold-500 text-white font-sans text-base font-medium rounded-md hover:bg-gold-600 transition-all whitespace-nowrap cursor-pointer"
            >
              <span>Danışmanlık Talep Edin</span>
              <i className="ri-arrow-right-line ml-3"></i>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Related Practice Areas */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="font-serif text-4xl text-navy-900 mb-4">İlgili Uzmanlık Alanları</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {practiceAreas
              .filter((a) => a.id !== area.id)
              .slice(0, 3)
              .map((relatedArea) => (
                <Link
                  key={relatedArea.id}
                  to={`/practice-areas/${relatedArea.slug}`}
                  className="group block bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={relatedArea.image}
                      alt={relatedArea.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-900/90 via-navy-900/50 to-transparent"></div>
                    <div className="absolute top-0 left-0 w-full h-1 bg-gold-500"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="w-10 h-10 flex items-center justify-center bg-gold-500 rounded-md mb-3">
                        <i className={`${relatedArea.icon} text-xl text-white`}></i>
                      </div>
                      <h3 className="font-sans text-lg font-semibold text-white mb-2">{relatedArea.title}</h3>
                      <div className="inline-flex items-center text-gold-500 text-sm font-medium">
                        <span>Daha Fazla Bilgi</span>
                        <i className="ri-arrow-right-line ml-2 group-hover:translate-x-2 transition-transform"></i>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PracticeAreaDetailPage;